/**
 * This deployment's signed-out identity, and the mapping onto the two shared
 * auth screens (#2296).
 *
 * Exactly the shape of `PortalSignIn` / `PortalReset`: `@solum/auth` owns the
 * composition, this file owns nothing but the mapping from a governed document
 * onto its slots. The Console had no such file, which is the whole defect —
 * `app.tsx` passed `SignIn` two behavioural props and no identity, so
 * `hasAside` was false and the legacy centred card was the only possible
 * outcome on every branded deployment.
 *
 * ── WHERE THE DOCUMENT COMES FROM, AND WHY IT IS NOT `getSetting` ──────────
 * `BrandSync` reads `platform|brand` through `core.get_setting`, which needs a
 * session — useless here by definition. The Portal's equivalent is
 * `portal.branding()`, granted to `anon` "precisely so this can render before a
 * session"; the Console's equivalent did not exist.
 *
 * It is NOT a sixth hand-rolled anon door. ADR H4 ruled that anonymous reach is
 * one registered public-projection contract, and rejected the static-artefact
 * alternative on correctness ("a revoked certificate stays verifiable until the
 * next publish"). So this reads `core.read_public_projection`, the single
 * governed door, which runs as a non-bypass role over a forced-RLS source.
 *
 * ── WHAT IS IN THE DOCUMENT, AND WHAT IS DELIBERATELY NOT ──────────────────
 * Ruled by Root (#2298 comment 73296, #2296 comment 73315): name, logo/mark,
 * favicon and the finite theme values — including the gradient — and nothing
 * else. No `headline`, no `tagline`, no `assurance`, no legal links, no
 * footnote: ADR 8 puts free text projected anonymously in the same leak class
 * as naming an identity provider.
 *
 * That has a consequence this file has to handle rather than inherit. `SignIn`
 * draws its brand panel on `headline || tagline || assurance || logo`, so a
 * deployment with a name and no logo would STILL collapse to the centred card —
 * the defect surviving its own fix. The display name is therefore mapped onto
 * `headline`, which Root accepted explicitly: the layout must not collapse
 * merely because a logo is absent, and a missing or broken logo degrades to the
 * governed name and gradient.
 */
import { useQuery } from '@tanstack/react-query'
import { readPublicBrand, type BrandOutcome } from './publicBrand'

/**
 * The projected document. Every field is optional and every field is CHECKED,
 * because the value arrives as jsonb: something shaped wrong must read as
 * absent rather than render `[object Object]` on a sign-in screen. The Portal's
 * mapping says the same thing about its own document, and for the same reason.
 */
export interface DeploymentBrand {
  name?: string
  logo?: string
  mark?: string
  favicon?: string
  colors?: { brand?: string; accent?: string }
  gradient?: { from?: string; to?: string }
  font?: { sans?: string }
  radius?: string
}

const text = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined

/** The host the browser actually asked for. It ROUTES and is never displayed —
 *  the projection refuses to echo it back, so nothing here can leak it. */
export function consoleHost(): string {
  return typeof window === 'undefined' ? '' : window.location.host
}

export function toDeploymentBrand(raw: unknown): DeploymentBrand | null {
  if (!raw || typeof raw !== 'object') return null
  const b = raw as Record<string, unknown>
  const colors = (b.colors ?? {}) as Record<string, unknown>
  const gradient = (b.gradient ?? {}) as Record<string, unknown>
  const font = (b.font ?? {}) as Record<string, unknown>
  const brand: DeploymentBrand = {
    name: text(b.name),
    logo: text(b.logo),
    mark: text(b.mark),
    favicon: text(b.favicon),
    colors: { brand: text(colors.brand), accent: text(colors.accent) },
    gradient: { from: text(gradient.from), to: text(gradient.to) },
    font: { sans: text(font.sans) },
    radius: text(b.radius),
  }
  // A document with no name is not an identity. Returning null here is what
  // makes the generic centred card the honest fallback rather than a panel
  // with nothing in it.
  return brand.name ? brand : null
}

/**
 * Read the deployment's public identity. Runs BEFORE any session exists.
 *
 * Deliberately NOT the Console SDK: that transport resolves a bearer token per
 * call and throws `AuthError: not signed in` before issuing a request, which is
 * measured in `publicBrand.ts`'s header and was the first wiring's defect.
 *
 * It must never block. An unconfigured deployment, an unknown host and an
 * unreachable database all end at the generic card — but they do not all end at
 * the same VALUE: `unavailable` is a fault and `none` is an honest absence, so
 * the fallback cannot hide broken wiring.
 */
export function useDeploymentBrand() {
  const live = Boolean(import.meta.env.VITE_SUPABASE_URL)
  return useQuery<BrandOutcome>({
    queryKey: ['public-brand', consoleHost()],
    // No try/catch here on purpose: `readPublicBrand` returns an OUTCOME rather
    // than throwing, so there is nothing to swallow and no way to turn a fault
    // into a success the cache then reports.
    queryFn: () => readPublicBrand(consoleHost()),
    enabled: live,
    staleTime: 5 * 60_000,   // the projection declares a 300s cache hint
    retry: false,
  })
}

/** The state a host can assert and a person never sees. `pending` is the frame
 *  before the read lands — named so the gate can mark it rather than render a
 *  brand panel that then changes, which is the identity flash. */
export function brandState(q: { isPending: boolean; data?: BrandOutcome }): string {
  return q.isPending ? 'pending' : q.data?.state ?? 'unavailable'
}

/** The document, or null when there is nothing to render. */
export function brandDoc(q: { data?: BrandOutcome }): DeploymentBrand | null {
  return q.data?.state === 'ok' ? toDeploymentBrand(q.data.doc) : null
}

/**
 * The mapping onto the shared screens' slots — the whole point of this file,
 * and deliberately the only place it happens, so `SignIn` and `ResetPassword`
 * cannot drift apart.
 *
 * `headline` carries the display NAME. See the header: it is what stops the
 * split composition collapsing when a deployment has no logo, and it is the
 * governed public name rather than free text.
 */
export function brandSlots(brand: DeploymentBrand | null | undefined) {
  if (!brand) return {}
  const logo = brand.logo ?? brand.mark
  return {
    logo,
    // A visual identity already says who this is. Repeating the deployment
    // name beside the Moonbase monogram turns the deliberately compact mark
    // into an accidental wordmark. Keep the name as the resilient fallback
    // only when no governed image exists.
    headline: logo ? undefined : brand.name,
  }
}
